import axios from "axios";
import { baseURL } from "../utils/constants";

const publicApiClient = axios.create({
  baseURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const unwrap = <T>(res: { data: any }): T =>
  (res?.data?.result ?? res?.data) as T;

export interface HospitalBranding {
  hospital_id: string;
  hospital_name: string;
  short_name?: string;
  logo_url?: string;
  theme_color?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  contact_number?: string;
  contact_info?: { phone?: string; address?: string; timing?: string };
}

export interface PublicFeatureFlags {
  hospital_id: string;
  video_consultation?: boolean;
  payments?: boolean;
  raghvendra_payment_flow?: boolean;
  prn_collection?: boolean;
  language_change_buttons?: string[];
  no_walkin_button?: boolean;
  hide_cancel_token_slot?: boolean;
  hide_booking_after_xmin?: boolean;
  booking_window_weeks?: number;
  booking_expiry_duration?: number;
  multiple_of_slot_time?: number;
  visit_type_instruction_override?: boolean;
  no_dual_booking?: boolean;
  multiple_queues?: boolean;
  auto_slot_selection?: boolean;
  specialization_flow?: boolean;
  no_extend_session?: boolean;
  confirmation_template?: string;
  // Gates the OTP flow on the public widget. Defaults to true on the
  // backend if missing (fail-secure) — frontend should treat anything
  // other than an explicit `false` as OTP-required.
  public_booking_otp_required?: boolean;
  // Master kill-switch. When false, render the unavailable screen and
  // do not allow the widget to proceed.
  public_booking_enabled?: boolean;
  // Numeric caps — null = "unlimited, don't enforce client-side."
  public_max_bookings_per_day?: number | null;
  public_max_bookings_per_phone_per_day?: number | null;
  public_max_advance_days?: number | null;
  public_otp_daily_send_ceiling?: number | null;
  [key: string]: unknown;
}

// Backend returns the raw enum literal "Slot_Token" with an underscore
// (admin sanitizer normalizes to "SlotToken", but our public route is the
// no-sanitizer variant — so keep the underscore form on the wire).
export type QueueType = "Slot" | "Token" | "Slot_Token" | "Session";

export interface PublicDoctorAvailability {
  availability_id: string;
  queue_type?: QueueType;
  day_of_week?: number;
  start_time?: string;
  end_time?: string;
  session_start_time?: string;
  session_end_time?: string;
  wait_time?: number | { hours?: number; minutes?: number };
  max_patients_per_slot?: number;
  is_active?: boolean;
}

export interface PublicDoctor {
  mapping_id: string;
  doctor_id: string;
  full_name: string;
  specialization?: string;
  qualification?: string;
  experience?: number | string;
  consultation_fee?: number | string;
  estimated_visit_time?: number;
  mode?: string;
  profile_picture?: string;
  photo_url?: string;
  image_url?: string;
  pre_payment?: boolean;
  video_amount?: string | number;
  physical_amount?: string | number;
  availabilities?: PublicDoctorAvailability[];
  [key: string]: unknown;
}

export interface PublicPatient {
  patient_id: string;
  contact_number: string;
  full_name: string;
  gender?: string | null;
  date_of_birth?: string | null;
  lang?: string | null;
  email?: string | null;
}

// Returns a header object that's safe to spread into axios `headers`.
// Omits Authorization entirely when no token — preventing a literal
// "Bearer undefined" string from being sent.
const authHeaders = (
  token?: string | null
): Record<string, string> | undefined =>
  token ? { Authorization: `Bearer ${token}` } : undefined;

export interface OtpSendResponse {
  otp_sent: boolean;
  expires_in_seconds: number;
}

export const sendBookingOtp = async (params: {
  contact_number: string;
  hospital_id: string;
}): Promise<OtpSendResponse> => {
  const res = await publicApiClient.post(
    `/public/booking/send-otp`,
    params
  );
  return unwrap<OtpSendResponse>(res);
};

export interface OtpVerifyResponse {
  booking_token: string;
  expires_in_seconds: number;
}

export const verifyBookingOtp = async (params: {
  contact_number: string;
  hospital_id: string;
  otp: string;
}): Promise<OtpVerifyResponse> => {
  const res = await publicApiClient.post(
    `/public/booking/verify-otp`,
    params
  );
  return unwrap<OtpVerifyResponse>(res);
};

// bookingToken is required when the hospital has
// `public_booking_otp_required = true` (default). For hospitals that have
// opted out, the token is optional — caller passes null/undefined and the
// Authorization header is omitted.
export const searchPublicPatient = async (
  contact_number: string,
  hospital_id: string,
  bookingToken?: string | null
): Promise<PublicPatient | null> => {
  const res = await publicApiClient.get(`/public/patient/search`, {
    params: { contact_number, hospital_id },
    headers: authHeaders(bookingToken),
  });
  const data = unwrap<PublicPatient[] | PublicPatient | null>(res);
  if (!data) return null;
  if (Array.isArray(data)) return data[0] ?? null;
  return data;
};

export const getPublicHospital = async (
  hospital_id: string
): Promise<HospitalBranding> => {
  const res = await publicApiClient.get(`/public/hospital/${hospital_id}`);
  return unwrap<HospitalBranding>(res);
};

export const getPublicFeatureFlags = async (
  hospital_id: string
): Promise<PublicFeatureFlags | null> => {
  const res = await publicApiClient.get(`/public/featureflags/${hospital_id}`);
  const data = unwrap<PublicFeatureFlags | PublicFeatureFlags[]>(res);
  if (Array.isArray(data)) return data[0] ?? null;
  return data ?? null;
};

export const getPublicOnlineDoctors = async (
  hospital_id: string,
  date: string
): Promise<PublicDoctor[]> => {
  const res = await publicApiClient.get(
    `/public/hospital/${hospital_id}/online-doctors`,
    { params: { date } }
  );
  const data = unwrap<PublicDoctor[] | { doctors?: PublicDoctor[] }>(res);
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as any).doctors)) return (data as any).doctors;
  return [];
};

export const getPublicDoctorAvailability = async (
  mapping_id: string
): Promise<PublicDoctorAvailability[]> => {
  const res = await publicApiClient.get(
    `/public/hospital/doctor/availability/${mapping_id}`
  );
  const data = unwrap<
    | PublicDoctorAvailability[]
    | { doctor_availability?: PublicDoctorAvailability[] }
  >(res);
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as any).doctor_availability))
    return (data as any).doctor_availability;
  return [];
};

export interface SlotTokenRequest {
  mapping_id: string;
  availability_id: string;
  booked_date: string; // YYYY-MM-DD
  start_time?: string;
  end_time?: string;
  number_of_people?: number;
  bookingType?: "booking" | "walk-in" | "video_consultation";
}

export interface PublicSlot {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
  available: boolean;
}

export interface PublicToken {
  token_number: number;
  available: boolean;
}

const parseHm = (t: string | undefined | null): number | null => {
  if (!t) return null;
  const [h, m] = String(t).split(":").map((s) => Number(s));
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
};

const fmtHm = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const waitTimeToMinutes = (
  w: number | { hours?: number; minutes?: number } | undefined | null
): number => {
  if (typeof w === "number") return w;
  if (w && typeof w === "object") {
    return (w.hours ? w.hours * 60 : 0) + (w.minutes || 0);
  }
  return 0;
};

interface BookedSlot {
  booked_slot_start?: string;
  booked_slot_end?: string;
}

const buildSlotsFromAvailability = (
  avail: PublicDoctorAvailability | undefined,
  booked: BookedSlot[]
): PublicSlot[] => {
  if (!avail) return [];
  const startM = parseHm(avail.start_time);
  const endM = parseHm(avail.end_time);
  const dur = waitTimeToMinutes(avail.wait_time);
  if (startM == null || endM == null || !dur || dur <= 0) return [];

  const cap = Number(avail.max_patients_per_slot) || 1;
  const counts = new Map<string, number>();
  for (const b of booked) {
    const k = (b?.booked_slot_start || "").slice(0, 5);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  }

  const out: PublicSlot[] = [];
  for (let m = startM; m + dur <= endM; m += dur) {
    const start = fmtHm(m);
    const end = fmtHm(m + dur);
    out.push({ start, end, available: (counts.get(start) || 0) < cap });
  }
  return out;
};

// Backend may return:
//  - flat array of numbers: [1, 2, 3, ...]
//  - object with tokens key: { tokens: [1, 2, 3, ...] }
//  - array of objects: [{ token_number, available }, ...]
// Backend filters booked tokens out — every returned token is treated as available.
const normalizeTokens = (raw: unknown): PublicToken[] => {
  const arr =
    Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object" && Array.isArray((raw as any).tokens)
      ? (raw as any).tokens
      : [];
  return (arr as unknown[])
    .map((entry): PublicToken | null => {
      if (typeof entry === "number") {
        return { token_number: entry, available: true };
      }
      if (entry && typeof entry === "object") {
        const e = entry as Record<string, unknown>;
        const num = Number(e.token_number ?? e.token ?? e.number);
        if (!Number.isFinite(num)) return null;
        return { token_number: num, available: e.available !== false };
      }
      return null;
    })
    .filter((t): t is PublicToken => t !== null);
};

export const getPublicSlots = async (
  payload: SlotTokenRequest
): Promise<PublicSlot[]> => {
  const res = await publicApiClient.post(`/public/slots`, payload);
  const data = unwrap<any>(res);

  // Already-shaped slot array.
  if (Array.isArray(data) && data.length && typeof data[0] === "object") {
    return data
      .map((s: any) => ({
        start: String(s.start || s.slot_start || "").slice(0, 5),
        end: String(s.end || s.slot_end || "").slice(0, 5),
        available: s.available !== false,
      }))
      .filter((s: PublicSlot) => s.start && s.end);
  }

  // { availability, booked_slots } — admin's raw shape, build client-side.
  if (data && typeof data === "object") {
    const availability: PublicDoctorAvailability | undefined = data.availability;
    const booked: BookedSlot[] = Array.isArray(data.booked_slots)
      ? data.booked_slots
      : [];
    if (availability) return buildSlotsFromAvailability(availability, booked);
    if (Array.isArray(data.slots)) {
      return data.slots
        .map((s: any) => ({
          start: String(s.start || s.slot_start || "").slice(0, 5),
          end: String(s.end || s.slot_end || "").slice(0, 5),
          available: s.available !== false,
        }))
        .filter((s: PublicSlot) => s.start && s.end);
    }
  }
  return [];
};

export const getPublicTokens = async (
  payload: SlotTokenRequest
): Promise<PublicToken[]> => {
  const res = await publicApiClient.post(`/public/tokens`, payload);
  return normalizeTokens(unwrap<any>(res));
};

export interface CreatePublicPatientPayload {
  hospital_id: string;
  full_name: string;
  contact_number: string;
  gender?: "Male" | "Female" | string;
  date_of_birth?: string | null;
  email?: string | null;
  address?: string | null;
  manager_contact_number?: string | null;
  lang?: string;
  marketing_consent?: boolean;
}

export const createPublicPatient = async (
  payload: CreatePublicPatientPayload,
  bookingToken?: string | null
): Promise<PublicPatient> => {
  const res = await publicApiClient.post(`/public/patient`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap<PublicPatient>(res);
};

// Per-queue payload contract (confirmed by backend):
//   - DO NOT send mapping_id (not a booking column)
//   - DO NOT send visit_type_id (not in schema)
//   - Slot       : booked_slot_start, booked_slot_end
//   - Token      : token_number
//   - Slot_Token : booked_slot_start, booked_slot_end, token_number
//   - Session    : session (int — count of session bookings)
//   - Optional fields must be OMITTED (undefined) when N/A — null fails the
//     validator's .optional().isInt() chain.
export interface CreatePublicBookingPayload {
  hospital_id: string;
  doctor_id: string;
  availability_id: string;
  patient_id: string;
  booked_date: string;
  booking_mode: "booking" | "walk-in";
  booking_type?: "physical" | "video_consultation";
  booking_lang?: string;
  is_confirmed?: boolean;
  symptoms?: string;
  comments?: string;
  queue_type: string;
  status: number;
  added_by?: string;
  booking_session_start_time?: string;
  booking_session_end_time?: string;
  booking_time?: string;
  prn_number?: string;
  // Queue-type-specific (omit when N/A):
  booked_slot_start?: string;
  booked_slot_end?: string;
  token_number?: number;
  session?: number;
  // visit_type_id mirrors AppointmentBooking's convention:
  //   1 = New
  //   2 = Follow Up
  // (See AppointmentBooking.tsx line ~1298 for the canonical mapping.)
  visit_type_id?: number;
}

export const createPublicBooking = async (
  payload: CreatePublicBookingPayload,
  bookingToken?: string | null
): Promise<unknown> => {
  const res = await publicApiClient.post(`/public/booking`, payload, {
    headers: authHeaders(bookingToken),
  });
  return unwrap(res);
};
